import axios from "axios";
import axiosInstance from "./apiinterseptor"; // Corrected import path
import { toast } from "react-toastify"; // Make sure this import exists
interface IJobAddressDetails {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
    landmark?: string;
}


interface SkillGroups {
  requiredSkills?: string[];
  preferredSkills?: string[];
  niceToHaveSkills?: string[];
}
// Request body type for creating job draft - matches backend ICreateJobDraftRequestBody
interface CreateJobDraftData {
     title: string;
  companyId: string;
  workType: 'on-site' | 'remote' | 'hybrid';
  location: string;
  addressDetails?: IJobAddressDetails;
  locationLink?: string;
  skills?: SkillGroups;  // assuming array of skill strings
  minExperience?: number;
  maxExperience?: number;
  qualificationRequirements?: string[]; // or maybe string[]? Depends on your data
  languageRequirements?: string[];      // same here, adjust as needed
  minAge?: number;
  maxAge?: number;
  employmentType?: string;             // maybe a union type if you have known options
  category?: string;
  minSalary?: number;
  maxSalary?: number;
  currency?: string;                   // e.g., 'USD', 'EUR'
  noOfOpenings?: number;
  shiftTimings?: string;               // maybe string or object, adjust if needed
  tenureInDays?: number;
  paymentFrequency?: string;           // e.g., 'monthly', 'weekly'
  commissionType?: string;             // e.g., 'fixed', 'percentage'
  commissionAmount?: number;
  commissionPercentage?: number;
  hiringNeed?: string;    
}

interface CreateJobDraftResponse {
  status: string;
  message: string;
  data: {
    jobId: string;
    title: string;
    status: 'DRAFTED';
  };
}

interface assignedJobQuery {
  page: string;
  limit:string;
  search?:string;
  fromDate?:string;
  toDate?:string
}

export const createJobDraft = async (jobData: CreateJobDraftData): Promise<CreateJobDraftResponse> => {
  try {
    const response = await axiosInstance.post('/jobs/draft', jobData);
    
    console.log('Job draft created successfully:', response.data);
    toast.success('Job draft created successfully');

    return response.data;
  } catch (error: any) {
    console.error('Failed to create job draft:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to create job draft. Please try again.');
    throw error;
  }
}
/**
 * Delete a drafted job (admin only)
 * @param jobId - The ID of the job to delete
 * @param token - Admin authentication token
 * @returns Promise with response data
 */
export async function deleteDraftJob(jobId: string, token: string) {
  if (!token) {
    token = localStorage.getItem('accessToken') || '';
  }
  if (!token) {
    throw new Error('No authentication token found. Please log in.');
  }
  console.log('Using token for DELETE request:', token);
  const response = await axiosInstance.delete(`jobs/${jobId}/draft`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
    },
  });
  // Axios throws on non-2xx status, so just return response.data
  return response.data;
}

// Interface for job list item
interface IJobListItem {
  id: string;
  title: string;
  companyName: string;
  companyLogoUrl?: string;
  location: string;
  workType: string;
  status: string;
  employmentType: string;
  createdAt: string;
  noOfOpenings: number;
}

// Interface for getJobs response
interface GetJobsResponse {
  status: string;
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
  results: number;
  data: {
    jobs: IJobListItem[];
  };
}

// Get jobs with optional filters
export const getJobs = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  status?: string;
  isExternal?: string;
  isActive?: string;
  isPublicApplication?: string;
  _t?: number; // For cache busting
  userRole?: string;
} = {}): Promise<GetJobsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.category) queryParams.append('category', params.category);
    if (params.status) queryParams.append('status', params.status);
    if (params.isExternal) queryParams.append('isExternal', params.isExternal);
    if (params.isActive) queryParams.append('isActive', params.isActive);
    if (params.isPublicApplication) queryParams.append('isPublicApplication', params.isPublicApplication);
    if (params._t) queryParams.append('_t', params._t.toString());
    
    console.log('🌐 API Request URL:', `/jobs?${queryParams.toString()}`);
    console.log('🌐 API Request params:', params);
    
    const response = await axiosInstance.get(params.userRole === 'TPO' ? `/jobs/tpo?${queryParams.toString()}` : `/jobs?${queryParams.toString()}`);
    
    console.log('🌐 Jobs API response status:', response.status);
    console.log('🌐 Jobs API response data:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch jobs:', error.response?.data?.message || error.message);
    console.error('🌐 Full error response:', error.response?.data);
    toast.error(error.response?.data?.message || 'Failed to fetch jobs. Please try again.');
    throw error;
  }
}

// Update your job creation functions with better logging:

export const updateJobRequirements = async (jobId: string, data: any) => {
  try {
    const apiUrl = `/jobs/${jobId}/requirements`;
    
    console.log('🔄 STEP 2: Updating Job Requirements');
    console.log('📝 Job ID:', jobId);
    console.log('📦 Request Data:', data);
    
    // Send data directly without wrapping in requirements object
    const response = await axiosInstance.put(apiUrl, data);
    
    console.log('✅ Requirements updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to update job requirements:', error.response?.data?.message || error.message);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Only ADMINs can perform this action.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found. Please check the job ID.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update job requirements. Please try again.');
    }
    throw error;
  }
};

export const updateJobTerms = async (jobId: string, data: any) => {
  try {
    const apiUrl = `/jobs/${jobId}/terms`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;
    
    console.log('🔄 STEP 3: Updating Job Terms');
    console.log('📍 API URL:', fullUrl);
    console.log('📝 Job ID:', jobId);
    console.log('📦 Request Data:', data);
    
    // Send the comprehensive data directly
    const response = await axiosInstance.put(apiUrl, data);
    
    console.log('✅ Terms updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to update job terms:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Only ADMINs can perform this action.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found. Please check the job ID.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update job terms. Please try again.');
    }
    throw error;
  }
};

export const createSubJob = async (data: any) => {
  try {
    const response = await axiosInstance.post('/subjobs',data);
    console.log("Current user details fetched:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};

export const editSubJob = async (id: any,data: any) => {
  try {
    const response = await axiosInstance.patch(`/subjobs/${id}`,data);
    console.log("Current user details fetched:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};

export const getSubJobList = async (id:string,role:string,params:any) => {
  try {
    const response = await axiosInstance.get(role === 'ADMIN' ? `/subjobs` : `/subjobs?postedByUserId=${id}&page=${params.page}&limit=${params.limit}&search=${params.search===undefined?'':params.search}`);
    console.log("Current user details fetched:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};


export const getSubJobDetails = async (jobId: any) => {
  try {
    const response = await axiosInstance.get(`/subjobs/${jobId}`);
    console.log("Current jobs details fetched:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};


export const getSubJobAppliedList = async (query:{search?:string, selectedDateFrom?:string, selectedDateTo?:string,skip?:number,status?:string}) => {
  try {
    const response = await axiosInstance.get(`/subjobs/applied`,{ params: query });
    console.log("Current subjob details fetched:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};

export const getPublicJobAppliedList = async (query:{search?:string, selectedDateFrom?:string, selectedDateTo?:string,skip?:number,status?:string}) => {
  try {
    const response = await axiosInstance.get(`/jobs/public`,{ params: query });
    console.log("Current subjob details fetched:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};


export const updateSubJobApplication = async (id:string, status:string, rejectionReason?: string) => {
  try {
    const payload = {
      status,
      id,
      ...(rejectionReason && { rejectionReason })
    }
    const response = await axiosInstance.patch(`/subjobs/applied`,payload);
    console.log("update subjob status:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};

export const updatePublicJobApplication = async (id:string, status:string, rejectionReason?: string) => {
  try {
    const payload = {
      status,
      id,
      ...(rejectionReason && { rejectionReason })
    }
    const response = await axiosInstance.patch(`/jobs/public`,payload);
    console.log("update subjob status:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch current user:", error.response?.data?.message);
    throw error;
  }
};


export const editJob = async (jobId: string, data: any) => {
  try {
    let apiUrl = `/jobs/${jobId}/publish`;
    const payload = {
      ...data,
      status: 'published'
    };

    const response = await axiosInstance.put(apiUrl, payload);
    console.log('✅ Job updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error(error.response?.data?.message || 'Access denied. You do not have permission to edit this job.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found. Please check the job ID.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to publish job. Please try again.');
    }
    throw error;
  }
}

export const publishJob = async ( data: any) => {
  try {
     const apiUrl = `/jobs/publish`;

    const payload = {
      ...data,
      status: 'published' // Ensure the job is marked as published
    };

    const response = await axiosInstance.post(apiUrl, payload);

    console.log('✅ Job published successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to publish job:', error.response?.data?.message || error.message);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Only ADMINs can perform this action.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found. Please check the job ID.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to publish job. Please try again.');
    }
    throw error;
  }
};

// Add this interface for job assignment request
interface IAssignJobRequestBody {
  assignToUserIds: string[];
}

// Add this interface for the assignment response
interface IAssignJobResponse {
  status: string;
  message: string;
  data: {
    jobId: string;
    assignedTo: Array<{
      id: string;
      username: string;
      role: string;
    }>;
  };
}

export const assignJobTpoApi = async (userId: string, jobIds: string[], addedJobIds?: string[], removedJobIds?: string[]): Promise<IAssignJobResponse> => {
  try {
    const apiUrl = `/jobs/${userId}/assign-tpo`;
    if (!jobIds || !Array.isArray(jobIds) ) {
      throw new Error('jobIds is required and must be a non-empty array');
    }
    const response = await axiosInstance.post(apiUrl, { 
      jobIds,
      addedJobIds: addedJobIds || [],
      removedJobIds: removedJobIds || []
    });
    console.log("Job assigned successfully:", response.data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to assign job:", error);
    throw error;
  }
};

// Update the assignJob function with proper typing
export const assignJob = async (jobId: string, data: IAssignJobRequestBody): Promise<IAssignJobResponse> => {
  try {
    const apiUrl = `/jobs/${jobId}/assign`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;
    
    console.log('🔄 STEP 5: Assigning Job');
    console.log('📍 API URL:', fullUrl);
    console.log('📝 Job ID:', jobId);
    console.log('📦 Request Data:', data);
    
    // Validate that assignToUserIds is provided and is an array
    if (!data.assignToUserIds || !Array.isArray(data.assignToUserIds) || data.assignToUserIds.length === 0) {
      throw new Error('assignToUserIds is required and must be a non-empty array');
    }
    
    const response = await axiosInstance.post(apiUrl, data);
    
    console.log('✅ Job assigned successfully:', response.data);
    console.log('📋 Assigned to users:', response.data.data?.assignedTo);
    toast.success(`Job assigned successfully to ${response.data.data?.assignedTo?.length || 0} user(s)`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to assign job:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 400) {
      toast.error('Invalid request. Please check the selected users.');
    } else if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Only ADMINs can perform this action.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found. Please check the job ID.');
    } else if (error.response?.status === 422) {
      toast.error('Validation error. Please check the assignment data.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to assign job. Please try again.');
    }
    throw error;
  }
};

// Add this function to get actual assignable users
export const getAssignableUsers = async (jobId: string) => {
  try {
    console.log('Fetching assignable users for job ID:', jobId);
    
    const response = await axiosInstance.get(`/jobs/${jobId}/assignable-users`);
    
    console.log('Assignable users fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch assignable users:', error.response?.data?.message || error.message);
    console.error('Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch assignable users.');
    }
    throw error;
  }
};

// Add this function to your apiclient.tsx
export const getJobDetailsById = async (jobId: string) => {
  try {
    console.log('🔄 Fetching job details for ID:', jobId);
    
    const response = await axiosInstance.get(`/jobs/${jobId}`);
    
    console.log('✅ Job details fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch job details:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch job details.');
    }
    throw error;
  }
};


export const getLoggedInUserJobs = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
} = {}) => {
  try {
    console.log('🔄 Fetching logged-in user jobs');
    console.log('📦 Params:', params);
    
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    
    const apiUrl = `/jobs/my-jobs?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;
    
    console.log('📍 API URL:', fullUrl);
    
    const response = await axiosInstance.get(apiUrl);
    
    console.log('✅ User jobs fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch user jobs:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. You may not have permission to view assigned jobs.');
    } else if (error.response?.status === 404) {
      toast.error('No jobs found or endpoint not available.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch user jobs.');
    }
    throw error;
  }
};


export const updateJob = async (jobId: string, updateData: any) => {
  try {
    console.log('🔄 Updating job with ID:', jobId);
    console.log('📦 Update payload:', updateData);
    
    const response = await axiosInstance.patch(`/jobs/${jobId}`, updateData);
    
    console.log('✅ Job updated successfully:', response.data);
    toast.success('Job updated successfully');
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to update job:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Only ADMINs can perform this action.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found. Please check the job ID.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to update job. Please try again.');
    }
    throw error;
  }
};

// Make sure this function exists and is working
export const updateJobArchiveStatus = async (jobId: string, isArchived: boolean) => {
  try {
    console.log('🔄 Updating job archive status with ID:', jobId);
    console.log('📦 Archive payload:', { isArchived });
    
    const response = await axiosInstance.patch(`/jobs/${jobId}/archive-status`, {
      isArchived
    });
    
    console.log('✅ Job archive status updated successfully:', response.data);
    const action = isArchived ? 'archived' : 'unarchived';
    toast.success(`Job has been successfully ${action}`);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to update job archive status:', error);
    // Error handling...
    throw error;
  }
};

export const updateCampusDriveStatusApi = async (jobId: string, eligibleForCampusDrive: boolean) => {
  try {
    const response = await axiosInstance.patch(`/jobs/${jobId}/campus-drive-status`, { eligibleForCampusDrive });
    console.log('✅ Campus drive status updated successfully:', response.data);
    toast.success("Campus drive status updated successfully!");
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to update campus drive status:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    throw error;
  }
};


export const updateActiveInactiveStatusApi = async (jobId: string, isActive: boolean) => {
  try {
    const response = await axiosInstance.patch(`/jobs/${jobId}/active-inactive-status`, { isActive });
    console.log('✅ Active inactive status updated successfully:', response.data);
    toast.success("Active inactive status updated successfully!");
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to update active inactive status:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    throw error;
  }
};

export const getAssignableJobs = async (
  userId: string,
  params: { search?: string; page?: number; limit?: number } = {}
) => {
  try {
    const response = await axiosInstance.get(`/jobs/assignable-jobs/${userId}`, {
      params,
    });
    console.log('✅ Assignable jobs fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch assignable jobs:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    throw error;
  }
};

// Get jobs list for selection dropdowns (bypasses role restrictions)
export const getJobsForSelection = async (params: {
  search?: string;
  _t?: number; // For cache busting
} = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params._t) queryParams.append('_t', params._t.toString());
    
    console.log('🔄 Fetching jobs for selection dropdown');
    console.log('📍 API URL:', `/jobs/list-for-selection/get?${queryParams.toString()}`);
    
    const response = await axiosInstance.get(`/jobs/list-for-selection/get?${queryParams.toString()}`);
    
    console.log('✅ Jobs for selection fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch jobs for selection:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch jobs for selection.');
    }
    throw error;
  }
};
export const unassignUser = async (userId: string,jobId:string) => {
  try {
    const response = await axiosInstance.post(`/jobs/${jobId}/${userId}/unassign`);
    console.log('✅ User unassigned successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to unassign user:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Only ADMINs can perform this action.');
    } else if (error.response?.status === 404) {
      toast.error('User not found. Please check the user ID.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to unassign user. Please try again.');
    }
    throw error;
  }
}

export function deleteJob(id: string): any {
  throw new Error("Function not implemented.");
}


export const getAssignedJobsForHr = async (urlQuery: assignedJobQuery) => {
  try {
    console.log('🔄 Fetching assigned job details',urlQuery);
     const queryParams = new URLSearchParams();
      
    if (urlQuery.page) queryParams.append('page', urlQuery.page);
    if (urlQuery.limit) queryParams.append('limit', urlQuery.limit);
    if (urlQuery.search) queryParams.append('search', urlQuery.search);
    if (urlQuery.fromDate) queryParams.append('selectedDateFrom', urlQuery.fromDate);
    if (urlQuery.toDate) queryParams.append('selectedDateTo', urlQuery.toDate);




    const response = await axiosInstance.get(`/hr/assigned-list?${queryParams.toString()}`);
    
    console.log('✅ Job details fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch Fetching assigned job details:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied.');
    } else if (error.response?.status === 404) {
      toast.error('Job not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch job details.');
    }
    throw error;
  }
};

export const triggerAssignedJobsForAdmin = async () => {
  try {
    const response = await axiosInstance.post('/hr/assigned-list/trigger');
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to trigger candidate of the day:', error.response?.data?.message || error.message);
    toast.error(error.response?.data?.message || 'Failed to trigger candidate of the day.');
    throw error;
  }
};

// Get jobs assigned to a specific user
export const getJobsAssignedToUser = async (userId: string) => {
  try {
    console.log('🔄 Fetching jobs assigned to user:', userId);
    const response = await axiosInstance.get(`/jobs/user/${userId}/assigned`);
    console.log('✅ Jobs assigned to user fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch jobs assigned to user:', error.response?.data?.message || error.message);
    
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.');
    } else if (error.response?.status === 403) {
      toast.error('Access denied. Only ADMINs can view assigned jobs.');
    } else if (error.response?.status === 404) {
      toast.error('User not found.');
    } else {
      toast.error(error.response?.data?.message || 'Failed to fetch assigned jobs.');
    }
  }
}

export const getCoordinatedJobs = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  userId?: string;
} = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.userId) queryParams.append('userId', params.userId);
    
    const response = await axiosInstance.get(`/jobs/coordinated-jobs?${queryParams.toString()}`);
    
    // Return the API response data structure which contains status and data fields
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch coordinated jobs:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    throw error;
  }
};


export const getPendingApprovalJobs = async (search?: string) => {
  try {
    const params = new URLSearchParams();
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const queryString = params.toString();
    const url = `/jobs/pending-approval-jobs${queryString ? `?${queryString}` : ''}`;
    const response = await axiosInstance.get(url);
    console.log('✅ Pending approval jobs fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to fetch pending approval jobs:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    throw error;
  }
};

export const updateJobApprovalStatus = async (jobId: string, status: 'published' | 'rejected', rejectionReason?: string) => {
  try {
    const body: { status: string; rejectionReason?: string } = { status };
    if (status === 'rejected' && rejectionReason) {
      body.rejectionReason = rejectionReason;
    }
    const response = await axiosInstance.patch(`/jobs/${jobId}/approval-status`, body);
    console.log('✅ Job approval status updated successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Failed to update job approval status:', error.response?.data?.message || error.message);
    console.error('🔍 Full error response:', error.response?.data);
    throw error;
  }
};